import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Lock, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import logo from "@/assets/logo.png";

const INCLUDES = [
  "30 guided daily lessons",
  "Daily check ins & progress trends",
  "Personalised self management toolkit",
  "Full access · cancel anytime",
];

const PLANS = {
  yearly: { key: "reset_yearly", price: "£47.99", per: "per year", label: "Annual", note: "Save over £11 vs monthly", badge: "Best value" },
  monthly: { key: "reset_monthly", price: "£4.99", per: "per month", label: "Monthly", note: "Easy way to start", badge: "" },
};

export default function Checkout() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("yearly");

  // If a logged-in customer already has access, skip
  useEffect(() => {
    if (user && user.has_access && user.role !== "admin") {
      navigate(user.onboarding_complete ? "/home" : "/onboarding");
    }
  }, [user, navigate]);

  const startCheckout = async (userId) => {
    const { data } = await api.post("/payments/checkout", {
      lookup_key: PLANS[plan].key,
      origin_url: window.location.origin,
      user_id: userId,
    });
    window.location.href = data.checkout_url;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      let userId = user && user.id;
      if (!user) {
        const newUser = await register(firstName, email, password);
        userId = newUser.id;
      }
      await startCheckout(userId);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const alreadyLoggedIn = user && user.role !== "admin";

  return (
    <div className="min-h-screen bg-app flex flex-col" data-testid="checkout-page">
      <div className="max-w-md w-full mx-auto px-6 py-10 flex-1 flex flex-col justify-center">
        <Link to="/" className="mb-8 flex justify-center">
          <img src={logo} alt="Meno Flames and Finesse" className="h-24 w-auto" />
        </Link>

        {user && user.access_state === "expired" && (
          <div className="mb-5 bg-white border-2 border-[var(--brand)] rounded-2xl p-5 text-center" data-testid="trial-ended-banner">
            <div className="w-11 h-11 rounded-full bg-sage flex items-center justify-center mx-auto">
              <Lock size={18} className="text-brand" />
            </div>
            <h2 className="font-serif text-xl font-semibold text-ink mt-3">Your free trial has ended</h2>
            <p className="text-ink-soft text-sm mt-1.5">
              Subscribe to keep your access and all the progress you've made. Choose the plan that suits you.
            </p>
          </div>
        )}

        {/* Plan selector */}
        <div className="grid grid-cols-2 gap-3 mb-4" data-testid="plan-selector">
          {["yearly", "monthly"].map((k) => {
            const p = PLANS[k];
            const active = plan === k;
            return (
              <button
                key={k}
                type="button"
                data-testid={`plan-${k}`}
                onClick={() => setPlan(k)}
                className={`relative text-left rounded-2xl p-4 border-2 transition-all ${active ? "border-[var(--brand)] bg-sage" : "border-subtle bg-white"}`}
              >
                {p.badge && (
                  <span className="absolute -top-2 right-3 bg-[var(--amber)] text-brand text-[10px] font-bold px-2 py-0.5 rounded-full">{p.badge}</span>
                )}
                <div className="text-ink-muted text-xs font-semibold">{p.label}</div>
                <div className="font-serif text-2xl font-bold text-ink mt-1">{p.price}</div>
                <div className="text-ink-muted text-xs">{p.per}</div>
                <div className={`text-[11px] mt-1.5 font-medium ${active ? "text-brand" : "text-ink-muted"}`}>{p.note}</div>
              </button>
            );
          })}
        </div>

        {/* Product summary */}
        <div className="bg-brand rounded-3xl p-6 text-white mb-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs font-semibold tracking-wide" style={{ color: "var(--amber)" }}>
                {plan === "yearly" ? "ANNUAL SUBSCRIPTION" : "MONTHLY SUBSCRIPTION"}
              </div>
              <h1 className="font-serif text-2xl font-bold mt-1">The 30 Day Menopause Reset</h1>
            </div>
            <div className="text-right">
              <div className="font-serif text-3xl font-bold">{PLANS[plan].price}</div>
              <div className="text-white/60 text-xs">{PLANS[plan].per}</div>
            </div>
          </div>
          <div className="mt-5 space-y-2">
            {INCLUDES.map((i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-white/85">
                <Check size={15} style={{ color: "var(--amber)" }} strokeWidth={3} /> {i}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-3xl card-shadow p-7">
          <h2 className="font-serif text-xl font-semibold text-ink">
            {alreadyLoggedIn ? "Choose your plan" : "Create your account"}
          </h2>
          <form onSubmit={submit} className="mt-5 space-y-4">
            {!alreadyLoggedIn && (
              <>
                <div>
                  <label className="text-sm font-semibold text-ink">First name</label>
                  <input
                    data-testid="checkout-firstname"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="mt-1.5 w-full rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)]"
                    placeholder="Sarah"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-ink">Email</label>
                  <input
                    data-testid="checkout-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5 w-full rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)]"
                    placeholder="you@email.com"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-ink">Password</label>
                  <input
                    data-testid="checkout-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="mt-1.5 w-full rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)]"
                    placeholder="At least 6 characters"
                  />
                </div>
              </>
            )}
            {error && (
              <div data-testid="checkout-error" className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">
                {error}
                {error.toLowerCase().includes("already exists") && (
                  <Link to="/login" className="block mt-1 font-semibold underline">Log in instead</Link>
                )}
              </div>
            )}
            <button
              data-testid="checkout-submit"
              disabled={busy}
              className="w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              <Lock size={16} /> {busy ? "Redirecting to payment…" : `Subscribe & Start Reset · ${plan === "yearly" ? "£47.99/yr" : "£4.99/mo"}`}
            </button>
          </form>
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-ink-muted">
            <ShieldCheck size={14} className="text-brand" /> Secure payment by Stripe · {plan === "yearly" ? "Billed £47.99 a year" : "Billed £4.99 a month"} · Cancel anytime
          </div>
          {!alreadyLoggedIn && (
            <p className="text-center text-sm text-ink-soft mt-4">
              Already have access?{" "}
              <Link to="/login" className="text-brand font-semibold">Log in</Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
